import { useEffect, useRef } from 'react';

import { TOOL_CONST } from '@/constant';
import { pcdManager } from '@/manager';
import { useLabelingBoxContext } from '@/provider';
import {
  usePcdCacheStore,
  usePcdFileStore,
  usePcdRenderingStore,
  useTaskFrameStore,
  useToolSettingStore,
} from '@/store';
import { logger } from '@/util';
import appWorker from '@/worker';

const VIEW_MODE_LIST = [
  TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT,
  TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE,
  TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP,
] as const;

const usePcdManager = () => {
  const [pcdIndex, isPlaying, pcdCollectionRerenderID] = usePcdRenderingStore(
    state => [state.pcdIndex, state.isPlaying, state.pcdCollectionRerenderID],
  );
  const path = usePcdFileStore(state => state.path);
  const [allFrameList, selectedTask] = useTaskFrameStore(state => [
    state.allFrameList,
    state.selectedTask,
  ]);
  const { labelingBoxList } = useLabelingBoxContext();
  const { isFixedIntensity, isPcdPointColorVisible, pointSize } =
    useToolSettingStore();
  const isInitializedRef = useRef(false);
  const currentPcdIndexRef = useRef(pcdIndex);
  const timeoutIdRef = useRef(-1 as unknown as NodeJS.Timeout);

  useEffect(() => {
    isInitializedRef.current = false;
  }, [isFixedIntensity]);

  useEffect(() => {
    if (isInitializedRef.current || !allFrameList[pcdIndex]) return;

    const pcdData = usePcdRenderingStore.getState().getCurrentPcdData();

    if (!pcdData || !pcdData.pointList.length) return;

    pcdManager.setGeometry(pcdData);

    isInitializedRef.current = true;

    if (isPlaying) {
      clearTimeout(timeoutIdRef.current);
      return;
    }

    if (!isPlaying) {
      clearTimeout(timeoutIdRef.current);

      timeoutIdRef.current = setTimeout(() => {
        const pcdPath = `${path}/${allFrameList[pcdIndex].filename}`;

        usePcdCacheStore
          .getState()
          .getPcdStatus(pcdPath)
          .then(pcdStatus => {
            if (pcdStatus.optimized) return;

            logger.performance(
              () =>
                appWorker
                  .request('pcd', 'transformQuadTree', {
                    frameSeq: pcdIndex + 1,
                    pointListBuffer: pcdData.pointList.buffer as ArrayBuffer,
                  })
                  .then(async value => {
                    if (!value) return;

                    const pcdPath = `${path}/${allFrameList[pcdIndex].filename}`;
                    const newPcdData = { ...pcdData, pointList: value };

                    usePcdCacheStore.getState().setPcd(pcdPath, newPcdData);
                    usePcdCacheStore
                      .getState()
                      .setPcdStatus(selectedTask!.pcdPath, pcdPath, {
                        ...pcdStatus,
                        optimized: true,
                      });

                    if (currentPcdIndexRef.current === pcdIndex) {
                      pcdManager.updateGeometry(newPcdData);

                      pcdManager.emit('sortedGeometry');
                    } else {
                      usePcdRenderingStore
                        .getState()
                        .setPcdData(pcdIndex, newPcdData, { skipRender: true });
                    }
                  })
                  .catch(error => {
                    console.group('[usePcdManager.transformQuadTree] Error');
                    console.warn(error);
                    console.groupEnd();
                  }),
              `[transformQuadTree] index: ${pcdIndex}, length: ${pcdData.pointList.length}`,
            );
          });
      }, 2500);
    }
  }, [
    allFrameList,
    labelingBoxList,
    pcdIndex,
    isPlaying,
    path,
    pcdCollectionRerenderID,
  ]);

  useEffect(() => {
    isInitializedRef.current = false;
    currentPcdIndexRef.current = pcdIndex;
  }, [pcdIndex]);

  useEffect(() => {
    const fstPointsize = pointSize + 1.5;

    pcdManager.setMaterialUniform({
      uniformMap: {
        bPcdPointColorVisible: isPcdPointColorVisible,
        fSize: pointSize,
        bFixedIntensity: isFixedIntensity,
      },
    });

    VIEW_MODE_LIST.forEach(viewMode => {
      pcdManager.setMaterialUniform({
        viewMode,
        uniformMap: {
          fSize: fstPointsize,
          bFixedIntensity: isFixedIntensity,
        },
      });
    });
  }, [isFixedIntensity, isPcdPointColorVisible, pointSize]);

  useEffect(() => {
    return () => {
      pcdManager.cleanup();
      clearTimeout(timeoutIdRef.current);
    };
  }, []);
};

export default usePcdManager;
